import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, FileText } from "lucide-react";
import { DraftSourceDocumentChip } from "@/components/draft/DraftSourceDocumentChip";
import { useContractTypes } from "@/hooks/useSupabase";
import type { DraftMatterDetail } from "@/types/drafting";

interface DraftMatterContextBarProps {
  matter: DraftMatterDetail;
}

export function DraftMatterContextBar({ matter }: DraftMatterContextBarProps) {
  const { contractTypes } = useContractTypes();
  const [expanded, setExpanded] = useState(false);

  const contractTypeLabel = useMemo(() => {
    if (!matter.contract_type) return null;
    const match = contractTypes.find((type) => type.value === matter.contract_type);
    return match?.name ?? matter.contract_type;
  }, [contractTypes, matter.contract_type]);

  const roleLabel = matter.legal_role_name ?? null;
  const hasDetails =
    Boolean(matter.background_info?.trim()) ||
    Boolean(matter.agent_instructions?.trim()) ||
    Boolean(matter.source_documents?.length);

  return (
    <div className="mb-4 rounded-xl border border-[var(--lm-border-card)] bg-white dark:border-slate-700 dark:bg-slate-950/45">
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <FileText className="h-4 w-4 shrink-0 text-violet-500 dark:text-violet-300" />
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-xs text-[var(--lm-foreground-strong)] dark:text-slate-200">
          {contractTypeLabel ? (
            <span className="rounded-full border border-[var(--lm-border-subtle)] px-2 py-0.5 dark:border-slate-600">
              {contractTypeLabel}
            </span>
          ) : null}
          {roleLabel ? (
            <span className="text-[var(--lm-muted)] dark:text-slate-400">
              Assigned counsel: <span className="text-[var(--lm-foreground-strong)] dark:text-slate-200">{roleLabel}</span>
            </span>
          ) : null}
          {matter.jurisdiction ? (
            <span className="text-[var(--lm-muted)] dark:text-slate-400">
              Governing law:{" "}
              <span className="text-[var(--lm-foreground-strong)] dark:text-slate-200">{matter.jurisdiction}</span>
            </span>
          ) : null}
        </div>
        {hasDetails ? (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary"
          >
            {expanded ? "Hide context" : "Show context"}
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        ) : null}
      </div>

      {expanded ? (
        <div className="space-y-3 border-t border-[var(--lm-border-subtle)] px-4 py-3 dark:border-slate-700">
          {matter.source_documents?.length ? (
            <div>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-500">
                Source documents
              </p>
              <div className="flex flex-wrap gap-2">
                {matter.source_documents.map((doc) => (
                  <DraftSourceDocumentChip
                    key={doc.id}
                    docType={doc.doc_type}
                    fileName={doc.file_name}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {matter.background_info?.trim() ? (
            <div>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-500">
                Background
              </p>
              <p className="whitespace-pre-wrap text-xs leading-relaxed text-[var(--lm-foreground-strong)] dark:text-slate-300">
                {matter.background_info}
              </p>
            </div>
          ) : null}

          {matter.agent_instructions?.trim() ? (
            <div>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-500">
                Special instructions
              </p>
              <p className="whitespace-pre-wrap text-xs leading-relaxed text-[var(--lm-foreground-strong)] dark:text-slate-300">
                {matter.agent_instructions}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
